// /plugins/docs/:slug, the plugin documentation shell: grouped sidebar nav,
// content column, an "On this page" rail with scroll tracking, and prev/next
// footer links. Page content + ordering live in docs/registry.tsx.

import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronLeft } from 'lucide-react';
import { DOC_PAGES } from './docs/registry';

const GROUPS = [...new Set(DOC_PAGES.map(p => p.group))];

function useActiveSection(slug: string, ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    // The active section is the last heading at or above the reading line
    // (120px clears the sticky header). A handful of rect reads per scroll
    // event is cheap at this heading count.
    const compute = () => {
      let current = ids[0] ?? null;
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (atBottom) {
        // Short final sections may never cross the reading line.
        current = ids[ids.length - 1] ?? null;
      } else {
        for (const id of ids) {
          const el = document.getElementById(id);
          if (el && el.getBoundingClientRect().top <= 120) current = id;
        }
      }
      setActive(current);
    };
    compute();
    window.addEventListener('scroll', compute, { passive: true });
    return () => window.removeEventListener('scroll', compute);
  }, [slug, ids]);

  return active;
}

export default function PluginsDocs() {
  const { slug } = useParams();
  const location = useLocation();
  const page = DOC_PAGES.find(p => p.slug === (slug ?? DOC_PAGES[0].slug));

  const sectionIds = useMemo(() => (page ? page.sections.map(s => s.id) : []), [page]);
  const active = useActiveSection(page?.slug ?? '', sectionIds);

  useEffect(() => {
    if (location.hash) {
      document.getElementById(location.hash.slice(1))?.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
    // Scroll only when the page changes, not on hash-only updates (the browser
    // already handles in-page anchor clicks natively).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.slug]);

  if (!page) return <Navigate to="/plugins/docs" replace />;

  const idx = DOC_PAGES.indexOf(page);
  const prev = idx > 0 ? DOC_PAGES[idx - 1] : null;
  const next = idx < DOC_PAGES.length - 1 ? DOC_PAGES[idx + 1] : null;
  const Body = page.component;

  return (
    <div className="lg:grid lg:grid-cols-[190px_minmax(0,1fr)] xl:grid-cols-[190px_minmax(0,1fr)_180px] lg:gap-10">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:block">
        <nav className="sticky top-24 space-y-6">
          <Link
            to="/plugins"
            className="flex items-center gap-1 px-2 text-[12px] text-textMuted hover:text-textPrimary transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            All plugins
          </Link>
          {GROUPS.map(group => (
            <div key={group}>
              <p className="px-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-textMuted">
                {group}
              </p>
              <ul className="mt-2 space-y-0.5">
                {DOC_PAGES.filter(p => p.group === group).map(p => (
                  <li key={p.slug}>
                    <Link
                      to={`/plugins/docs/${p.slug}`}
                      className={`block rounded-md px-2 py-1.5 text-[13px] transition-colors ${
                        page.slug === p.slug
                          ? 'bg-[var(--color-surface)] text-textPrimary'
                          : 'text-textSecondary hover:text-textPrimary hover:bg-white/[0.03]'
                      }`}
                    >
                      {p.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <div className="min-w-0">
        {/* Page switcher (mobile and tablet) */}
        <div className="lg:hidden -mx-6 px-6 mb-6 overflow-x-auto">
          <div className="flex w-max gap-1.5">
            {DOC_PAGES.map(p => (
              <Link
                key={p.slug}
                to={`/plugins/docs/${p.slug}`}
                className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-[12px] transition-colors ${
                  page.slug === p.slug
                    ? 'border-transparent bg-[var(--color-surface)] text-textPrimary'
                    : 'border-[var(--color-borderSubtle)] text-textSecondary hover:text-textPrimary'
                }`}
              >
                {p.title}
              </Link>
            ))}
          </div>
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-textMuted">
          {page.group}
        </p>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">{page.title}</h1>
        <p className="mt-2 text-textSecondary leading-relaxed max-w-2xl">{page.description}</p>

        <div className="mt-8">
          <Body />
        </div>

        <div className="mt-12 grid gap-3 border-t border-[var(--color-borderSubtle)] pt-6 sm:grid-cols-2">
          {prev ? (
            <Link
              to={`/plugins/docs/${prev.slug}`}
              className="settings-card flex items-center gap-3 p-4 hover:bg-white/[0.03] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-textMuted shrink-0" />
              <div className="min-w-0">
                <p className="text-[11px] text-textMuted">Previous</p>
                <p className="text-[13px] font-medium text-textPrimary truncate">{prev.title}</p>
              </div>
            </Link>
          ) : (
            <div />
          )}
          {next && (
            <Link
              to={`/plugins/docs/${next.slug}`}
              className="settings-card flex items-center justify-end gap-3 p-4 text-right hover:bg-white/[0.03] transition-colors"
            >
              <div className="min-w-0">
                <p className="text-[11px] text-textMuted">Next</p>
                <p className="text-[13px] font-medium text-textPrimary truncate">{next.title}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-textMuted shrink-0" />
            </Link>
          )}
        </div>
      </div>

      {/* On-this-page rail (wide screens) */}
      <aside className="hidden xl:block">
        <nav className="sticky top-24">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-textMuted">
            On this page
          </p>
          <ul className="mt-2.5 space-y-1.5 border-l border-[var(--color-borderSubtle)]">
            {page.sections.map(s => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className={`-ml-px block border-l pl-3 text-[12px] leading-snug transition-colors ${
                    active === s.id
                      ? 'border-[var(--color-accent)] text-textPrimary'
                      : 'border-transparent text-textMuted hover:text-textPrimary'
                  }`}
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </div>
  );
}
